import { compact } from 'lodash'
import { gqlFetchList } from 'app/lib/graphqlClient'
import type { ContactJson } from 'app/types/entities'
import { contactNameTerms } from 'lib/contactSearch'
import { debug } from 'lib/logging'

/**
 * A possible duplicate, and how hard we push back on it.
 *
 * `blocking` is reserved for the same person: the name matches too, not just the
 * contact method. Blocking on a shared phone alone would break the commonest
 * legitimate case in a practice, a parent's mobile sitting on every child's
 * record. Dev data has 28 same-surname/different-first-name pairs on a shared
 * number against 109 identical-name-and-phone pairs, so the two tiers are both
 * real and worth telling apart.
 */
export type DuplicateMatch = { contact: ContactJson; blocking: boolean }

export type ContactIdentity = {
  cellPhone?: string
  email?: string
  locationId?: string
}

/** Case, spacing and punctuation insensitive, so "bob  smith" matches "Bob Smith". */
export const normalizeName = (name?: string | null): string =>
  (name ?? ``)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ` `)
    .trim()

/**
 * Strong signals only: an exact cell phone or email hit inside the selected
 * location. Requiring an exact name as well blocked common name collisions
 * while missing the real duplicates (same phone, different spelling of the name).
 */
export function duplicateSearches({ cellPhone, email, locationId }: ContactIdentity): string[] {
  if (!locationId) return []
  return compact([
    cellPhone && `locationId:"${locationId}" AND cellPhone:(${cellPhone})`,
    email && `locationId:"${locationId}" AND email:("${email}")`,
  ])
}

export function classifyDuplicate(
  hits: ContactJson[],
  name: string,
  dismissedIds: string[],
): DuplicateMatch | undefined {
  const samePerson = hits.find((hit) => normalizeName(hit.name) === normalizeName(name))
  // Not dismissible: the name and the contact method both match, so this is the
  // same person and creating a second record for them is the thing we prevent.
  if (samePerson) return { contact: samePerson, blocking: true }
  const advisory = hits.find((hit) => hit.id && !dismissedIds.includes(hit.id))
  return advisory ? { contact: advisory, blocking: false } : undefined
}

/**
 * Runs through `gqlFetchList` rather than the contact store on purpose: the store
 * writes its `list` observable, which is the same one backing the contacts table,
 * so searching from a create form used to replace the user's table data.
 */
export async function lookupDuplicate(
  name: string,
  identity: ContactIdentity,
  dismissedIds: string[],
): Promise<DuplicateMatch | undefined> {
  const searches = duplicateSearches(identity)
  if (searches.length === 0) return undefined
  const results = await Promise.all(
    searches.map((search) => gqlFetchList<ContactJson>(`contacts`, { search, pagination: { limit: 10 } })),
  )
  const hits = results.flatMap((result) => result.hits)
  debug(`new-contact`, `duplicate hits`, hits)
  return classifyDuplicate(hits, name, dismissedIds)
}

const MIN_SUGGESTION_CHARS = 2

/** ES search for "is this person already here?", every typed word required somewhere in the name. */
export function nameSuggestionSearch(name: string): string | undefined {
  if (name.trim().length < MIN_SUGGESTION_CHARS) return undefined
  return contactNameTerms(name).join(` AND `)
}

/**
 * Existing contacts whose name contains what was typed so far. Deliberately not
 * scoped to a location: one person is routinely a contact at several locations,
 * and the caller on the line may be any of them.
 */
export async function findContactsByName(name: string, limit: number): Promise<ContactJson[]> {
  const search = nameSuggestionSearch(name)
  if (!search) return []
  const result = await gqlFetchList<ContactJson>(`contacts`, { search, pagination: { limit } })
  return result.hits
}
